import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

/**
 * Simplified Volume Profile.
 *
 * Computes a rolling volume-at-price histogram over the last `lookback` bars.
 * Outputs three key levels as horizontal lines:
 *   POC  — Point of Control: price level with the highest volume
 *   VAH  — Value Area High: upper edge of the 70% value area (centred on POC)
 *   VAL  — Value Area Low:  lower edge of the 70% value area
 *
 * Note: Full horizontal bar rendering is not possible with LWC's built-in series
 * types.  The POC / VAH / VAL lines give the most actionable information.
 */
const CONFIG: IndicatorConfig = {
  id: 'volprofile',
  name: 'Volume Profile',
  shortName: 'VP',
  category: 'professional',
  overlay: true,
  params: [
    { key: 'lookback', label: 'Lookback Bars', type: 'number', default: 200, min: 20, max: 1000 },
    { key: 'bins', label: 'Price Bins', type: 'number', default: 50, min: 10, max: 200 },
  ],
  lines: [
    { key: 'poc', label: 'POC', color: '#ff6d00', width: 2 },
    { key: 'vah', label: 'VAH', color: '#26a69a', width: 1, style: 'dashed' },
    { key: 'val', label: 'VAL', color: '#ef5350', width: 1, style: 'dashed' },
  ],
}

interface VPResult {
  poc: number
  vah: number
  val: number
}

function computeVP(slice: OHLCV[], numBins: number): VPResult | null {
  if (slice.length === 0) return null

  let minPrice = Infinity
  let maxPrice = -Infinity
  for (const c of slice) {
    if (c.l < minPrice) minPrice = c.l
    if (c.h > maxPrice) maxPrice = c.h
  }
  if (maxPrice <= minPrice) return null

  const binSize = (maxPrice - minPrice) / numBins
  const volumeAtBin = new Float64Array(numBins)

  for (const c of slice) {
    const binLow = Math.max(0, Math.floor((c.l - minPrice) / binSize))
    const binHigh = Math.min(numBins - 1, Math.floor((c.h - minPrice) / binSize))
    const spread = binHigh - binLow + 1
    const volPerBin = c.v / spread
    for (let b = binLow; b <= binHigh; b++) {
      volumeAtBin[b] += volPerBin
    }
  }

  // Point of Control
  let pocBin = 0
  for (let b = 1; b < numBins; b++) {
    if (volumeAtBin[b] > volumeAtBin[pocBin]) pocBin = b
  }
  const poc = minPrice + (pocBin + 0.5) * binSize

  // Value Area: expand up/down from POC until 70% of total volume is captured
  const totalVol = volumeAtBin.reduce((s, v) => s + v, 0)
  const target = totalVol * 0.7
  let vahBin = pocBin
  let valBin = pocBin
  let areaVol = volumeAtBin[pocBin]

  while (areaVol < target) {
    const upVol = vahBin + 1 < numBins ? volumeAtBin[vahBin + 1] : 0
    const downVol = valBin - 1 >= 0 ? volumeAtBin[valBin - 1] : 0
    if (upVol === 0 && downVol === 0) break
    if (upVol >= downVol && vahBin + 1 < numBins) {
      vahBin++
      areaVol += volumeAtBin[vahBin]
    } else if (valBin - 1 >= 0) {
      valBin--
      areaVol += volumeAtBin[valBin]
    } else {
      vahBin++
      areaVol += volumeAtBin[vahBin]
    }
  }

  return {
    poc,
    vah: minPrice + (vahBin + 1) * binSize,
    val: minPrice + valBin * binSize,
  }
}

class VolumeProfile extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const lookback = Math.max(20, Number(params['lookback']) || 200)
    const numBins = Math.max(10, Number(params['bins']) || 50)
    const n = candles.length

    const poc: (number | null)[] = new Array<number | null>(n).fill(null)
    const vah: (number | null)[] = new Array<number | null>(n).fill(null)
    const val: (number | null)[] = new Array<number | null>(n).fill(null)

    // Only compute where we have enough bars; use a rolling window
    for (let i = lookback - 1; i < n; i++) {
      const slice = candles.slice(i - lookback + 1, i + 1)
      const result = computeVP(slice, numBins)
      if (result) {
        poc[i] = result.poc
        vah[i] = result.vah
        val[i] = result.val
      }
    }

    return {
      lines: [
        { key: 'poc', values: poc },
        { key: 'vah', values: vah },
        { key: 'val', values: val },
      ],
    }
  }
}

registerIndicator(VolumeProfile, CONFIG)
